import { GlobalPageLoadExperience } from '@atlassian/ufo';
import { collabStateManager } from '@databases/collab-state-manager';
import { aiFirstCreationStreamingState } from '@databases/database-ai-first-creation-state';
import { useDatabaseStreamState } from '@databases/database-ai-first-creation-ui';
import { DatabaseBlankObjectExperienceFooter } from '@databases/database-ai-first-creation-ui/DatabaseBlankObjectExperienceFooter';
import { getVersionId, useDatabaseContent } from '@databases/database-state';
import { isAIFCStagingAreaView } from '@databases/database-state';
import { setDatabaseContentReady } from '@databases/database-state/state/initial-database-loading-state';
import { isStandalone as isStandaloneCheck } from '@databases/modes';
import { sendContentReady } from '@databases/native-embed-editor-plugin';
import { AsyncOperationStatus } from '@databases/redux-types';
import { appStoreUtils } from '@databases/redux/store';
import { getBridgeAPI } from '@databases/utils/database/bridge';
import { isNonBrokenDatabase } from '@databases/utils/database/utils';
import { useIsFeatureEnabled } from '@databases/utils/hooks/useIsFeatureEnabled';
import {
  selectDatabaseByContentIdOptional,
  selectDatabaseByContentIdStatus
} from '@databases/utils/redux/databases/selectors';
import useView from '@databases/views/useView';
import React, { type FunctionComponent, useEffect } from 'react';
import { useSnapshot } from 'valtio';

import DatabaseContent from './common/components/database-content/DatabaseContent.tsx';
import DatabaseContentSkeleton from './common/components/database-content/DatabaseContentSkeleton.tsx';
import { DatabaseInitialLoadingState } from './common/components/database-skeletons/DatabaseInitialLoadingState.tsx';
import { type DatabaseActionKey } from './common/components/database/DatabaseActions.tsx';
import { DatabaseError } from './DatabaseError.tsx';
import { useOmittedMediaNotice } from './use-omitted-media-notice.ts';
import { useUnassociatedMediaRepair } from './use-unassociated-media-repair.ts';

const isStandalone = isStandaloneCheck();

const actions: DatabaseActionKey[] = ['import', 'print', 'help', 'copy', 'feedback'];
if (isStandalone) {
  actions.push('lock');
}

interface DatabaseContentLoaderProps {
  onDataLoaded?: () => void;
}

const DatabaseContentLoader: FunctionComponent<DatabaseContentLoaderProps> = ({
  onDataLoaded
}) => {
  const { contentId } = useDatabaseContent();
  const versionId = getVersionId();
  const isDbAiFirstCreationEnabled = useIsFeatureEnabled('dbAiFirstCreation');
  const { firstLayoutProcessed } = useSnapshot(aiFirstCreationStreamingState);
  const { isStreaming } = useDatabaseStreamState();
  const isInAIFCStagingAreaView = isAIFCStagingAreaView();
  const { view } = useView();

  const databaseStatus = appStoreUtils.useEqualSelector((state) =>
    selectDatabaseByContentIdStatus(state, { contentId })
  );
  const database = appStoreUtils.useEqualSelector((state) =>
    selectDatabaseByContentIdOptional(state, { contentId })
  );
  const hasMultipleViews =
    database && isNonBrokenDatabase(database) ? database.views.length > 1 : false;
  const isEmptyDatabase =
    database && isNonBrokenDatabase(database)
      ? Boolean(collabStateManager.getCollabManager(contentId)?.database.isEmpty) &&
        !hasMultipleViews
      : false;
  const isDatabaseLoaded = databaseStatus?.status === AsyncOperationStatus.SUCCEEDED;

  useOmittedMediaNotice({
    contentId,
    database: database && isNonBrokenDatabase(database) ? database : undefined,
    isLoaded: isDatabaseLoaded
  });
  useUnassociatedMediaRepair({ contentId, isLoaded: isDatabaseLoaded });

  useEffect(() => {
    const logMetrics = async () => {
      GlobalPageLoadExperience.mark('database-load-end');
      GlobalPageLoadExperience.success({ force: true });
      (await getBridgeAPI()).onContentLoadSucceeded?.({});
    };

    if (databaseStatus?.status === AsyncOperationStatus.SUCCEEDED) {
      logMetrics();
      onDataLoaded?.();
    }
  }, [databaseStatus?.status, contentId, onDataLoaded]);

  const isAIStreamingWithoutLayout =
    isDbAiFirstCreationEnabled &&
    isStreaming &&
    isInAIFCStagingAreaView &&
    // firstLayoutProcessed is always false in standalone, so we should only
    // check for databases in AIFC staging area
    !firstLayoutProcessed;
  const shouldShowSkeleton = !isDatabaseLoaded || isAIStreamingWithoutLayout;
  useEffect(() => {
    if (!shouldShowSkeleton) {
      setDatabaseContentReady();
      sendContentReady();
    }
  }, [shouldShowSkeleton]);

  if (databaseStatus?.status === AsyncOperationStatus.ERROR) {
    return <DatabaseError serializedError={databaseStatus.error} />;
  }

  if (shouldShowSkeleton) {
    return view.id && !isAIStreamingWithoutLayout ? (
      <DatabaseContentSkeleton layoutType={view.settings.layout.type} />
    ) : (
      <DatabaseInitialLoadingState />
    );
  }

  return (
    <DatabaseContent
      contentId={contentId}
      isContentEditable={!versionId}
      actions={actions}
      footer={
        <DatabaseBlankObjectExperienceFooter
          contentId={contentId}
          isDbAiFirstCreationEnabled={isDbAiFirstCreationEnabled}
          isDatabaseLoaded={isDatabaseLoaded}
          isEmptyDatabase={isEmptyDatabase}
          isInAIFCStagingAreaView={isInAIFCStagingAreaView}
        />
      }
    />
  );
};

export default DatabaseContentLoader;
